import React, { useState } from "react";
import { Box, Typography, Button, TextField, CircularProgress } from "@mui/material";
import { QRCodeSVG } from "qrcode.react";
import {
  setUpTOTP,
  verifyTOTPSetup,
  updateMFAPreference,
  fetchUserAttributes,
} from "@aws-amplify/auth";

const ISSUER = "WhisperBI";

// Flujo de enrolamiento TOTP 100% client-side (Amplify Auth v6 contra
// Cognito directo, mismo criterio ya usado por SendPhoneVerificationCode.js/
// VerifyPhoneCode.js) -- no existe ni hace falta ninguna Lambda propia para
// setUpTOTP/verifyTOTPSetup/updateMFAPreference. Se usa tanto desde
// ProfileDrawer.js (activación voluntaria) como desde MfaSetupDialog.js
// (activación forzada cuando la cuenta exige MFA).
const TotpEnrollmentForm = ({ onEnrolled }) => {
  const [step, setStep] = useState("idle"); // idle | qr | verifying
  const [setupUri, setSetupUri] = useState("");
  const [sharedSecret, setSharedSecret] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleStart = async () => {
    setLoading(true);
    setError("");
    try {
      const totpSetupDetails = await setUpTOTP();
      let accountName = "usuario";
      try {
        const attributes = await fetchUserAttributes();
        accountName = attributes.email || accountName;
      } catch {
        // No bloquea el enrolamiento si no se puede leer el email.
      }
      setSetupUri(totpSetupDetails.getSetupUri(ISSUER, accountName).toString());
      setSharedSecret(totpSetupDetails.sharedSecret);
      setStep("qr");
    } catch (err) {
      setError(err.message || "No se pudo iniciar la configuración de MFA.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      await verifyTOTPSetup({ code });
      await updateMFAPreference({ totp: "PREFERRED" });
      onEnrolled?.();
    } catch (err) {
      setError(err.message || "Código incorrecto. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  if (step === "idle") {
    return (
      <Box>
        <Typography variant="body2" color="gray" sx={{ mb: 2 }}>
          Protege tu cuenta con una app autenticadora (Google Authenticator, Authy, etc.).
        </Typography>
        {error && (
          <Typography variant="body2" sx={{ color: "var(--wb-danger)", mb: 2 }}>
            {error}
          </Typography>
        )}
        <Button
          variant="contained"
          color="primary"
          fullWidth
          disabled={loading}
          sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
          onClick={handleStart}
        >
          {loading ? <CircularProgress size={20} /> : "Activar verificación en dos pasos"}
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="gray" sx={{ mb: 2 }}>
        Escanea el código con tu app autenticadora, o ingresa el secreto manualmente.
      </Typography>
      <Box sx={{ display: "flex", justifyContent: "center", mb: 2, background: "var(--wb-surface-white)", p: 2, borderRadius: 2 }}>
        <QRCodeSVG value={setupUri} size={180} />
      </Box>
      <Typography variant="caption" color="gray" sx={{ display: "block", mb: 2, wordBreak: "break-all" }}>
        Secreto manual: {sharedSecret}
      </Typography>
      <form onSubmit={handleVerify}>
        <TextField
          fullWidth
          label="Código de 6 dígitos"
          value={code}
          onChange={(event) => setCode(event.target.value)}
          sx={{ mb: 2, input: { color: "var(--wb-text-strong)" } }}
          InputLabelProps={{ style: { color: "var(--wb-x-text-aaaaaa, #aaa)" } }}
        />
        {error && (
          <Typography variant="body2" sx={{ color: "var(--wb-danger)", mb: 2 }}>
            {error}
          </Typography>
        )}
        <Button
          type="submit"
          variant="contained"
          color="success"
          fullWidth
          disabled={loading || code.length === 0}
          sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
        >
          {loading ? <CircularProgress size={20} /> : "Confirmar y activar"}
        </Button>
      </form>
    </Box>
  );
};

export default TotpEnrollmentForm;
